import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid2";
import { Control, Controller } from "react-hook-form";
import { debounce, isEmpty } from "lodash";
import dayjs from "dayjs";

import { TabSessionStorageResponse } from "../../../models/api/TabSessionStorage";
import colors from "../../../styles/colors/colors";
import fontWeight from "../../../styles/mui/fontWeight";
import { FaExclamationIcon, FieldSpacing, SectionSpacing, StyleGridRow } from "../styles/style";
import { rules } from "../../../utils/validation/Validation";
import {
  displayFlex,
  flexColumn,
  flexRow,
} from "../../../styles/mui/styles/display";
import {
  Discipline,
  DisciplineList,
  ListOptions,
} from "../../../models/Master";
import DatePicker from "../../../components/FormPriorAuthReAuth/DatePicker";
import others from "../../../styles/colors/others";
import CheckboxDropdown from "../../../components/FormPriorAuthReAuth/CheckBoxDropdown";
import {
  addDaysToDate,
  currentDate,
  getSelectedTimeForSOC,
  getValue,
  is21OrTurning21Soon,
  isAbove21,
} from "../../../utils";
import { MyAccountModel } from "../../../models/MyAccount";
import { updateTabStatus } from "../../../constants/TabSessionStorage";
import { MaxAllowedSocDateActionDispatchTypes, Numbers, SlotsDispatchTypes } from "../../../constants/Referral";
import { TextConstants } from "../../../constants/Patient";
import {
  MemberInfoData,
  PatientInformation,
  PatientInformationView,
} from "../../../models/Patient";
import { IsRetroDetails, MaxAllowedSocDate, MaxAllowedSocDatePayload, Slots, SlotsPayload } from "../../../models/Referral";
import { DateFormats } from "../../../constants/Date";
import { formatDate, parseMaxAllowedSocDate } from "../../../utils/DateTime";
import { InformationText } from "../../../app/common/TextLabel";
import { getType } from "../../../constants/eligibility";
import BcbsTnRetroModal from "./BcbsTnRetroModal";
import { AETNA, AETNA_MIN_SOC_DATE, BCBSMI, BCBSMIMINSOCDATE } from "../../../constants/FunctionalUtils";

export interface PropsFromState {
  control: Control<any>;
  setValue: any;
  clearErrors: any;
  tabSessionStorage: TabSessionStorageResponse;
  masterCareTypeState: ListOptions[];
  disciplineListState: Discipline[];
  disciplineList: DisciplineList[] | undefined;
  reimbursementTypeId: number;
  loggedInUserState: MyAccountModel;
  reset: any;
  getValues: any;
  informationState: PatientInformationView;
  slotsState: Slots;
  patientInformationByIdState: PatientInformation;
  memberInfoDataState: MemberInfoData;
  isRetroDetailsState: IsRetroDetails;
  onModalConfirm: () => void;
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  maxAllowedSocDateState: MaxAllowedSocDate | null;
}
export interface PropsFromDispatch {
  getCareTypes: () => void;
  setValue: any;
  getDisciplineList: (eligibilityId: number, isPortal: boolean) => void;
  getSlots: (payload: SlotsPayload) => void;
  resetStates: (actionType: string, field?: string) => void;
  getIsRetroDetails: (startOfCareDate: string, userSpecificReceivedDateTime: string) => void;
  getMaxAllowedSocDate: (payload: MaxAllowedSocDatePayload) => void;
}
type AllProps = PropsFromState & PropsFromDispatch;

const RequestServices: React.FC<any> = ({
  control,
  masterCareTypeState,
  disciplineListState,
  getCareTypes,
  getDisciplineList,
  setValue,
  loggedInUserState,
  getValues,
  reset,
  patientInformationByIdState,
  getSlots,
  reimbursementTypeId,
  slotsState,
  memberInfoDataState,
  resetStates,
  getIsRetroDetails,
  isRetroDetailsState,
  onModalConfirm,
  modalOpen,
  setModalOpen,
  tabSessionStorage,
  getMaxAllowedSocDate,
  maxAllowedSocDateState
}: AllProps) => {
  const [isNurseToOpen, setIsNurseToOpen] = React.useState<boolean>(getValues().requestService.isNurseToOpen);
  const [stWarning, setSTWarning] = React.useState<boolean>(false);
  const [disciplineOptions, setDisciplineOptions] =
    React.useState<Discipline[]>(disciplineListState);

  const callSlots = (date: any) => {
    getSlots({
      patientEligibilityId: patientInformationByIdState?.patientEligibilityId,
      type: Numbers.ONE,
      startOfCareDate: formatDate(date, DateFormats.YYYY_MM_DD),
      reimbursementTypeId:
        isEmpty(reimbursementTypeId) || reimbursementTypeId === Numbers.ZERO
          ? ""
          : reimbursementTypeId,
    });
    if (
      getType({
        label: memberInfoDataState.healthPlan,
        value: memberInfoDataState.payerId,
      }) === Numbers.Four
    ) {
      getIsRetroDetails(formatDate(date, DateFormats.YYYY_MM_DD), dayjs().format("YYYY-MM-DDTHH:mm:ss"));
    }
  };

  useEffect(() => {
    getCareTypes();
    getDisciplineList(1, true);
    const email = getValue(loggedInUserState, "providerPortalUser.email");
    setValue("requestService.userCreated", email);
    setValue("requestService.fullNameCreated", email);
    setValue("requestService.psychologicalDiseasesDto", null);
  }, []);

  useEffect(() => {
    setDisciplineOptions(getDisciplineOptions());
      reset({
        ...getValues(),
        tabStatus: updateTabStatus(4, getValues("tabStatus")),
      });
  }, [disciplineListState]);

  useEffect(() => {
    if (modalOpen) {
      const retroFlag = isRetroDetailsState && getValue(isRetroDetailsState, "isRetro");
      if (!retroFlag) {
        onModalConfirm && onModalConfirm();
      }
    }
  }, [modalOpen, isRetroDetailsState]);

  useEffect(() => {
    setValue("requestService.isNurseToOpen", isNurseToOpen);
  }, [isNurseToOpen]);

  const onChangeHandler = (date: any) => {
    const { minDateTime, maxDateTime, selectedTime } = getSelectedTimeForSOC(
      currentDate(),
      date,
      false,
      getValue(memberInfoDataState, "healthPlan")
    );
    if (
      !isNaN(selectedTime) &&
      selectedTime >= minDateTime &&
      selectedTime <= maxDateTime
    ) {
      resetStates(SlotsDispatchTypes.SLOTS_RESET);
      setTimeout(() => {
        callSlots(date);
      }, 200);
    }
  };

  const getNotEligibleForSpeechTherapy = () => {
    if (!isEmpty(slotsState)) {
      const notEligibleForSpeechTherapy =
        is21OrTurning21Soon(
          memberInfoDataState.dob,
          slotsState.currentStartDate,
          slotsState.currentEndDate
        ) ||
        is21OrTurning21Soon(
          memberInfoDataState.dob,
          slotsState.nextStartDate,
          slotsState.nextStartDate
        );

      return notEligibleForSpeechTherapy;
    }
    return false;
  };

  const getDisciplineOptions = () => {
    const options =
      Array.isArray(disciplineListState) && disciplineListState.length > 0
        ? disciplineListState.filter(
            (singleOption: Discipline) =>
              !(
                singleOption.disciplineId === Numbers.Five &&
                TextConstants.MEDICAID ===
                  getValue(patientInformationByIdState, "memberInfo.product")
              ) &&
              !(
                !isEmpty(memberInfoDataState.dob) &&
                isAbove21(memberInfoDataState.dob) &&
                singleOption.disciplineId === Numbers.Four &&
                TextConstants.MEDICAID === getValue(patientInformationByIdState, "memberInfo.product")
              )
          )
        : [];

    return options;
  };

  useEffect(() => {
    if (
      getNotEligibleForSpeechTherapy() &&
      TextConstants.MEDICAID ===
        getValue(patientInformationByIdState, "memberInfo.product")
    ) {
      setSTWarning(true);
    } else {
      setSTWarning(false);
    }
  }, [slotsState]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        onChangeHandler(value);
      }, 500),
    []
  );

  const getMinimumDate = () => {
  const healthPlan = getValue(memberInfoDataState, "healthPlan");
  if (healthPlan === AETNA) {
    return dayjs(AETNA_MIN_SOC_DATE);
  }
  if (healthPlan === BCBSMI) {
    return dayjs(BCBSMIMINSOCDATE);
  }
  return addDaysToDate(currentDate(), -5, "day");
};

useEffect(() => {
  const patientEligibilityId = patientInformationByIdState?.patientEligibilityId;
  if (patientEligibilityId) {
    getMaxAllowedSocDate({ patientEligibilityId });
  }
}, [patientInformationByIdState?.patientEligibilityId]);

useEffect(() => {
  return () => {
    resetStates(MaxAllowedSocDateActionDispatchTypes.GET_MAX_ALLOWED_SOC_DATE_RESET);
  };
}, []);

const maxAllowedSocDate = parseMaxAllowedSocDate(
  maxAllowedSocDateState?.maxAllowedSocDate,
);

const maximumDate = maxAllowedSocDate
  ? maxAllowedSocDate
  : addDaysToDate(currentDate(), 30, "day");

  return (
    <Grid size={12} pt={2}>
      <Grid container>
        <Grid size={{ xs: 12 }} sx={SectionSpacing}>
          <Grid container sx={flexColumn}>
            <Grid size={{ xs: 12 }} sx={displayFlex}>
              <Typography
                variant="h6"
                fontWeight={fontWeight[5]}
                color={colors.fonts[20]}
                pr={"1rem"}
              >
                REQUEST SERVICES
              </Typography>
              <Divider orientation="vertical" variant="fullWidth" flexItem />
              <Typography
                variant="body1"
                fontWeight={fontWeight[2]}
                color={colors.fonts[2]}
                pl={"1rem"}
              >
                Please enter start of care date, select discipline(s) with home
                health orders and select a care type (if applicable).
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={flexRow}>
        <Grid size={{ xs: 4 }} sx={FieldSpacing}>
          <FormControl variant="standard">
            <DatePicker
              name="requestService.startOfCare"
              control={control}
              label={"Start of Care:"}
              helper={rules.socDateTimeRequired}
              allowFutureDate={true}
              minimumDate={getMinimumDate()}
              maximumdate={maximumDate} 
              onChangeHandler={(date: any) => debouncedSearch(date)}
            />
          </FormControl>
        </Grid>
        <Grid size={{ xs: 4 }} sx={FieldSpacing}>
          <Controller
            name="requestService.referralCareTypeDto"
            control={control}
            defaultValue={[]}
            render={({ field }) => (
              <CheckboxDropdown
                {...field}
                control={control}
                name="requestService.referralCareTypeDto"
                label="Care Type:"
                options={masterCareTypeState}
              />
            )}
          />
        </Grid>
        <Grid container spacing={2} sx={flexRow}>
          <Grid size={{ xs: 12 }} sx={FieldSpacing}>
            <Box sx={displayFlex} pb={"1rem"}>
              <Typography
                variant="h6"
                fontWeight={fontWeight[6]}
                color={colors.fonts[20]}
              >
                Which skilled disciplines are ordered for start of care? CHOOSE
                ALL THAT APPLY.
              </Typography>
              <Typography
                variant="subtitle2"
                fontWeight={fontWeight[2]}
                color={colors.red[103]}
                pl={"0.3rem"}
              >
                *
              </Typography>
            </Box>
            {getType({
              label: memberInfoDataState.healthPlan,
              value: memberInfoDataState.payerId,
            }) === Numbers.Four && (
              <Grid size={{ xs: 12 }} mr={2.5} pr={2} pb={2} sx={StyleGridRow}>
                <FaExclamationIcon />
                {InformationText(
                  TextConstants.BCBS_TN_WARNING,
                  fontWeight[5],
                  {},
                  colors.black[1],
                )}
              </Grid>
            )}
            <Controller
              name="requestService.discipline"
              control={control}
              defaultValue={[]}
              render={({ field, fieldState: { error } }) => (
                <>
                  <FormHelperText>
                    {error ? error.message : null}
                  </FormHelperText>
                  <FormGroup>
                    <Grid container spacing={2}>
                      {disciplineOptions &&
                        disciplineOptions.map((singleOption: Discipline) => (
                          <Grid
                            size={{ xs: 12, sm: 6, md: 4 }}
                            key={singleOption.disciplineId}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={
                                    Array.isArray(field.value) &&
                                    field.value.includes(
                                      singleOption.disciplineId
                                    )
                                  }
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    const currentValue = Array.isArray(
                                      field.value
                                    )
                                      ? field.value
                                      : [];
                                    const updated = checked
                                      ? [
                                          ...currentValue,
                                          singleOption.disciplineId,
                                        ]
                                      : currentValue.filter(
                                          (id: number) =>
                                            id !== singleOption.disciplineId
                                        );
                                    setIsNurseToOpen(
                                      updated.some(
                                        (id: number) => id === Numbers.Eight
                                      )
                                    );
                                    field.onChange(updated);
                                  }}
                                  disabled={
                                    (singleOption.disciplineId ===
                                      Numbers.ONE &&
                                      Array.isArray(field.value) &&
                                      field.value.includes(Numbers.Eight)) ||
                                    (singleOption.disciplineId ===
                                      Numbers.Eight &&
                                      Array.isArray(field.value) &&
                                      field.value.includes(Numbers.ONE))
                                  }
                                />
                              }
                              label={
                                <Box>
                                  <Typography
                                    variant="subtitle1"
                                    color={others.otherColors[63]}
                                    fontWeight={fontWeight[5]}
                                  >
                                    {singleOption.disciplineDescription}
                                  </Typography>
                                  {(singleOption.disciplineDescription ===
                                    "Medical Social Worker" ||
                                    singleOption.disciplineDescription ===
                                      "Home Health Aide") && (
                                    <Typography
                                      variant="body1"
                                      fontWeight={fontWeight[2]}
                                      color={colors.fonts[2]}
                                      pt={"0.2rem"}
                                    >
                                      (must be paired with SN, PT, OT or ST)
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                            {Array.isArray(field.value) &&
                              field.value.includes(singleOption.disciplineId) &&
                              singleOption.disciplineId === Numbers.Four &&
                              stWarning && (
                                <Typography
                                  variant="h6"
                                  color={colors.red[90]}
                                  fontWeight={fontWeight[3]}
                                  lineHeight={"1.5rem"}
                                >
                                  {TextConstants.DISCIPLINE_WARNING}
                                </Typography>
                              )}
                          </Grid>
                        ))}
                    </Grid>
                  </FormGroup>
                </>
              )}
              rules={rules.discipline}
            />
          </Grid>
        </Grid>
      </Grid>
      {(getType({
        label: memberInfoDataState.healthPlan,
        value: memberInfoDataState.payerId,
      }) === Numbers.Four) && (isRetroDetailsState.isRetro === true) && (
        <BcbsTnRetroModal
          open={!!modalOpen}
          onClose={() => setModalOpen(false)}
          onConfirm={() => onModalConfirm()}
          disciplineListState={disciplineListState}
          getValues={getValues}
          setValue={setValue}
          isRetroDetailsState={isRetroDetailsState}
          control={control}
          tabSessionStorage={tabSessionStorage}
        />
      )}
    </Grid>
  );
};

export default RequestServices;
